<template>
  <v-dialog
    :value="value"
    max-width="600"
    @input="$emit('input', $event)"
  >
    <v-card>
      <v-card-title class="success white--text">
        Reserva Criada com Sucesso!
        <v-spacer></v-spacer>
        <v-btn
          icon
          dark
          @click="$emit('input', false)"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="pt-4">
        <v-alert
          v-if="paymentConfirmed"
          type="success"
          text
        >
          Sua reserva #{{ reservationData && reservationData.reserve_id }} foi confirmada!
        </v-alert>
        <v-alert
          v-else
          type="info"
          text
        >
          Reserva #{{ reservationData && reservationData.reserve_id }} criada. Aguarde a confirmação do pagamento.
        </v-alert>
        <v-alert
          v-if="holdActive"
          type="info"
          outlined
          dense
          class="mb-3"
        >
          Conclua o pagamento em <strong>{{ holdCountdownLabel }}</strong> para garantir o quarto.
        </v-alert>
        <v-alert
          v-if="holdExpired && holdToken"
          type="error"
          outlined
          dense
          class="mb-3"
        >
          O tempo para pagamento expirou. Realize uma nova busca para tentar novamente.
        </v-alert>
        <v-alert
          v-if="paymentConfirmed"
          type="success"
          outlined
          dense
          class="mb-3"
        >
          Pagamento confirmado! Enviamos os detalhes para o seu e-mail.
        </v-alert>

        <!-- PIX -->
        <div
          v-if="paymentMethod === 'pix' && reservationData && reservationData.pix_qr_code && !paymentConfirmed"
        >
          <h3 class="mb-3">
            Pagamento via PIX
          </h3>
          <v-img
            :src="`data:image/png;base64,${reservationData.pix_qr_code}`"
            max-width="300"
            class="mx-auto mb-3"
          ></v-img>
          <v-text-field
            :value="reservationData.pix_copy_paste"
            label="Código PIX (Copiar e Colar)"
            readonly
            outlined
            dense
            append-icon="mdi-content-copy"
            @click:append="$emit('copy-pix', reservationData.pix_copy_paste)"
          ></v-text-field>
        </div>

        <!-- Boleto -->
        <div v-if="paymentMethod === 'boleto' && reservationData && reservationData.boleto_url && !paymentConfirmed">
          <h3 class="mb-3">
            Pagamento via Boleto
          </h3>
          <v-btn
            color="primary"
            block
            :href="reservationData.boleto_url"
            target="_blank"
          >
            <v-icon left>
              mdi-file-document-outline
            </v-icon>
            Visualizar Boleto
          </v-btn>
        </div>

        <!-- Cartão de Crédito/Débito -->
        <div
          v-if="(paymentMethod === 'cartao' || paymentMethod === 'debito') && reservationData && reservationData.invoice_url && !paymentConfirmed"
        >
          <h3 class="mb-3">
            <v-icon
              left
              color="primary"
            >
              mdi-credit-card
            </v-icon>
            Pagamento com {{ paymentMethod === 'cartao' ? 'Cartão de Crédito' : 'Cartão de Débito' }}
          </h3>

          <v-alert
            type="info"
            outlined
            class="mb-4"
          >
            <div class="d-flex align-center">
              <v-icon
                left
                color="primary"
              >
                mdi-shield-lock
              </v-icon>
              <div>
                <strong>Ambiente 100% Seguro</strong>
                <p class="mb-0 mt-1">
                  Clique no botão abaixo para acessar a página segura de pagamento.
                </p>
              </div>
            </div>
          </v-alert>

          <v-btn
            color="success"
            block
            large
            :href="reservationData.invoice_url"
            target="_blank"
            class="mb-3"
          >
            <v-icon left>
              mdi-lock
            </v-icon>
            Pagar Agora - Página Segura
          </v-btn>

          <v-alert
            type="success"
            dense
            outlined
          >
            <small>
              <v-icon
                small
                left
              >mdi-security</v-icon>
              <strong>Segurança garantida:</strong> Você será direcionado para um ambiente protegido onde seus dados
              ficam totalmente seguros. A confirmação do pagamento é automática.
            </small>
          </v-alert>
        </div>

        <!-- Pagamento confirmado -->
        <div
          v-if="paymentConfirmed"
          class="text-center"
        >
          <v-icon
            size="64"
            color="success"
          >
            mdi-check-circle
          </v-icon>
          <h3 class="mt-3 mb-2">
            Pagamento Aprovado!
          </h3>
          <p class="text--secondary">
            Sua reserva foi confirmada com sucesso.
          </p>
        </div>

        <v-divider class="my-4"></v-divider>

        <v-btn
          v-if="paymentConfirmed && reservationData && reservationData.voucher_url"
          color="primary"
          outlined
          block
          large
          :href="reservationData.voucher_url"
          target="_blank"
          download
          class="mb-3"
        >
          <v-icon left>
            mdi-file-pdf-box
          </v-icon>
          Baixar Voucher da Reserva
        </v-btn>

        <p class="text--secondary">
          Você receberá um e-mail de confirmação com todos os detalhes da reserva.
        </p>
      </v-card-text>
      <v-card-actions>
        <v-btn
          text
          @click="$emit('input', false)"
        >
          Fechar
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          @click="$emit('new-reservation')"
        >
          Fazer Nova Reserva
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'BookingPaymentDialog',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    reservationData: {
      type: Object,
      default: null,
    },
    paymentMethod: {
      type: String,
      default: 'pix',
    },
    paymentConfirmed: Boolean,
    holdActive: Boolean,
    holdCountdownLabel: {
      type: String,
      default: '--:--',
    },
    holdExpired: Boolean,
    holdToken: {
      type: String,
      default: null,
    },
  },
}
</script>
